import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, authHeaders, apiErrorMessage } from "@/lib/auth";
import { AdminLogin } from "@/pages/AdminLogin";
import { AdminChangePassword } from "@/pages/AdminChangePassword";
import {
  Briefcase, LogOut, Plus, Search, Copy, Trash2, Edit3, Eye, KeyRound,
  Save, X, ArrowLeft, Download, Users,
} from "lucide-react";
import { cn } from "@/lib/utils";

const EMP_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];
const EXP_LEVELS = ["Entry", "Mid", "Senior", "Lead"];
const JOB_STATUSES = ["draft", "published", "closed"];
const APP_STATUSES = ["new", "reviewing", "shortlisted", "rejected"];

const EMPTY_JOB = {
  title: "", slug: "", department: "Engineering", location: "Muscat, Oman",
  employment_type: "Full-time", experience_level: "Mid",
  summary: "", description: "",
  responsibilities: [""], requirements: [""], nice_to_have: [""],
  status: "draft", closes_at: "",
};

export default function AdminCareers() {
  const { user, logout } = useAuth();
  const [showChangePw, setShowChangePw] = useState(false);

  if (!user) return <AdminLogin title="Careers — Sign in" subtitle="Access the jobs & applications dashboard." />;
  if (user.must_change_password) return <AdminChangePassword forced onDone={() => window.location.reload()} />;
  if (showChangePw) return <AdminChangePassword onDone={() => setShowChangePw(false)} />;

  return <CareersDashboard user={user} logout={logout} onChangePw={() => setShowChangePw(true)} />;
}

function CareersDashboard({ user, logout, onChangePw }) {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | {job}
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterJobId, setFilterJobId] = useState("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true); setError("");
    try {
      const [j, a] = await Promise.all([
        api.get("/admin/jobs", authHeaders()),
        api.get("/admin/applications", authHeaders()),
      ]);
      setJobs(j.data.items);
      setApplications(a.data.items);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); return; }
      setError(apiErrorMessage(err, "Failed to load."));
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const filteredJobs = useMemo(() => jobs.filter((j) => {
    if (filterStatus !== "all" && j.status !== filterStatus) return false;
    if (!searchTerm.trim()) return true;
    return j.title.toLowerCase().includes(searchTerm.trim().toLowerCase());
  }), [jobs, filterStatus, searchTerm]);

  const filteredApps = useMemo(() =>
    applications.filter((a) => filterJobId === "all" || a.job_id === filterJobId),
    [applications, filterJobId]);

  if (editing) {
    return (
      <JobEditor
        job={editing === "new" ? EMPTY_JOB : editing}
        onCancel={() => setEditing(null)}
        onSaved={async () => { await load(); setEditing(null); }}
      />
    );
  }

  return (
    <section className="min-h-screen bg-wafa-cream/30" data-testid="admin-careers-page">
      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-6 lg:py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
              <Briefcase className="h-3.5 w-3.5" /> Careers dashboard
            </div>
            <h1 className="mt-1 font-heading text-3xl font-semibold text-wafa-deep sm:text-4xl">Jobs & Applications</h1>
            <p className="mt-1 text-xs text-wafa-ink/60">
              Signed in as <span className="font-mono">{user.email}</span> · role {user.role}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onChangePw} className="inline-flex items-center gap-1.5 rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm hover:bg-wafa-cream/40" data-testid="careers-change-pw">
              <KeyRound className="h-4 w-4" /> Change password
            </button>
            <button onClick={logout} data-testid="careers-logout" className="inline-flex items-center gap-1.5 rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm hover:bg-wafa-cream/40">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-1 rounded-md bg-white p-1 sm:w-fit">
          <TabButton active={tab === "jobs"} onClick={() => setTab("jobs")} testid="tab-jobs" icon={Briefcase}>Jobs ({jobs.length})</TabButton>
          <TabButton active={tab === "applications"} onClick={() => setTab("applications")} testid="tab-applications" icon={Users}>Applications ({applications.length})</TabButton>
        </div>

        {error ? <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="careers-error">{error}</div> : null}

        {tab === "jobs" ? (
          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setEditing("new")}
                data-testid="careers-new-job"
                className="inline-flex items-center gap-2 rounded-md bg-wafa-primary px-4 py-2 text-sm font-semibold text-white hover:bg-wafa-deep"
              >
                <Plus className="h-4 w-4" /> New job
              </button>
              <div className="ml-auto flex items-center gap-2">
                <div className="flex items-center gap-2 rounded-md border border-wafa-deep/10 bg-white px-3">
                  <Search className="h-4 w-4 text-wafa-ink/50" />
                  <input
                    placeholder="Search title…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="careers-search"
                    className="h-10 w-56 bg-transparent text-sm focus:outline-none"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  data-testid="careers-filter-status"
                  className="h-10 rounded-md border border-wafa-deep/10 bg-white px-3 text-sm focus:outline-none"
                >
                  <option value="all">All statuses</option>
                  {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-lg border border-wafa-deep/10 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-wafa-cream/40 text-left text-xs uppercase tracking-wider text-wafa-deep">
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Department</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Posted</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-wafa-deep/10">
                  {filteredJobs.map((j) => (
                    <tr key={j.id} data-testid={`job-row-${j.slug}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-wafa-deep">{j.title}</div>
                        <div className="text-xs text-wafa-ink/60">/{j.slug}</div>
                      </td>
                      <td className="px-4 py-3 text-wafa-ink/80">{j.department}</td>
                      <td className="px-4 py-3 text-wafa-ink/80">{j.location}</td>
                      <td className="px-4 py-3"><StatusPill status={j.status} /></td>
                      <td className="px-4 py-3 text-xs text-wafa-ink/60">
                        {j.posted_at ? new Date(j.posted_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {j.status === "published" ? (
                            <Link
                              to={`/company/careers/${j.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`job-preview-${j.slug}`}
                              className="rounded p-1.5 text-wafa-ink/60 hover:bg-wafa-cream/40 hover:text-wafa-deep"
                              title="Preview"
                            ><Eye className="h-4 w-4" /></Link>
                          ) : null}
                          <button
                            onClick={() => setEditing(j)}
                            data-testid={`job-edit-${j.slug}`}
                            className="rounded p-1.5 text-wafa-ink/60 hover:bg-wafa-cream/40 hover:text-wafa-deep"
                            title="Edit"
                          ><Edit3 className="h-4 w-4" /></button>
                          <button
                            onClick={async () => {
                              await api.post(`/admin/jobs/${j.id}/duplicate`, {}, authHeaders());
                              load();
                            }}
                            data-testid={`job-duplicate-${j.slug}`}
                            className="rounded p-1.5 text-wafa-ink/60 hover:bg-wafa-cream/40 hover:text-wafa-deep"
                            title="Duplicate"
                          ><Copy className="h-4 w-4" /></button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete "${j.title}"?`)) return;
                              await api.delete(`/admin/jobs/${j.id}`, authHeaders());
                              load();
                            }}
                            data-testid={`job-delete-${j.slug}`}
                            className="rounded p-1.5 text-wafa-ink/60 hover:bg-red-50 hover:text-red-700"
                            title="Delete"
                          ><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!busy && filteredJobs.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-wafa-ink/60">No jobs. Click "New job" to create one.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <ApplicationsTab
            applications={filteredApps}
            jobs={jobs}
            filterJobId={filterJobId}
            setFilterJobId={setFilterJobId}
            onStatusChange={async (id, status) => {
              await api.patch(`/admin/applications/${id}`, { status }, authHeaders());
              load();
            }}
          />
        )}
      </div>
    </section>
  );
}

function TabButton({ active, onClick, children, testid, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors",
        active ? "bg-wafa-deep text-white" : "text-wafa-ink/70 hover:bg-wafa-cream/40",
      )}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null} {children}
    </button>
  );
}

function StatusPill({ status }) {
  const cls = {
    draft: "bg-wafa-ink/10 text-wafa-ink/70",
    published: "bg-wafa-primary/10 text-wafa-primary",
    closed: "bg-wafa-tomato/10 text-wafa-tomato",
    new: "bg-wafa-sunshine/40 text-wafa-deep",
    reviewing: "bg-wafa-primary/10 text-wafa-primary",
    shortlisted: "bg-wafa-primary/20 text-wafa-deep",
    rejected: "bg-wafa-tomato/10 text-wafa-tomato",
  }[status] || "bg-wafa-ink/10 text-wafa-ink/70";
  return (
    <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", cls)} data-testid={`status-${status}`}>
      {status}
    </span>
  );
}

function ApplicationsTab({ applications, jobs, filterJobId, setFilterJobId, onStatusChange }) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-wafa-deep">Filter by job:</label>
        <select
          value={filterJobId}
          onChange={(e) => setFilterJobId(e.target.value)}
          data-testid="apps-filter-job"
          className="h-10 rounded-md border border-wafa-deep/10 bg-white px-3 text-sm focus:outline-none"
        >
          <option value="all">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
        </select>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-wafa-deep/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-wafa-cream/40 text-left text-xs uppercase tracking-wider text-wafa-deep">
            <tr>
              <th className="px-4 py-3">Applicant</th>
              <th className="px-4 py-3">Job</th>
              <th className="px-4 py-3">Received</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">CV</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-wafa-deep/10">
            {applications.map((a) => (
              <tr key={a.id} data-testid={`application-row-${a.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-wafa-deep">{a.name}</div>
                  <div className="text-xs text-wafa-ink/60">
                    <a href={`mailto:${a.email}`} className="hover:text-wafa-primary">{a.email}</a>
                    {a.phone ? ` · ${a.phone}` : ""}
                  </div>
                  {a.portfolio_url ? (
                    <div className="mt-0.5 text-xs">
                      <a href={a.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-wafa-primary hover:underline">{a.portfolio_url}</a>
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-wafa-ink/80">{a.job_title}</td>
                <td className="px-4 py-3 text-xs text-wafa-ink/60">{new Date(a.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <select
                    value={a.status || "new"}
                    onChange={(e) => onStatusChange(a.id, e.target.value)}
                    data-testid={`application-status-${a.id}`}
                    className="h-8 rounded-md border border-wafa-deep/10 bg-white px-2 text-xs focus:outline-none"
                  >
                    {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications/${a.id}/cv`}
                    onClick={(e) => {
                      // Force auth header via fetch + blob so we can hit an auth-protected endpoint
                      e.preventDefault();
                      const t = localStorage.getItem("wafa.admin.token");
                      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications/${a.id}/cv`, {
                        headers: { Authorization: `Bearer ${t}` },
                      }).then((r) => r.blob()).then((blob) => {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = a.cv_filename || `cv-${a.name}${a.cv_ext || ""}`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                      });
                    }}
                    data-testid={`application-cv-${a.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-wafa-primary hover:text-wafa-deep"
                  >
                    <Download className="h-3.5 w-3.5" /> {a.cv_filename}
                  </a>
                </td>
              </tr>
            ))}
            {applications.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-wafa-ink/60">No applications yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobEditor({ job, onCancel, onSaved }) {
  const [form, setForm] = useState({
    ...EMPTY_JOB,
    ...job,
    responsibilities: job.responsibilities?.length ? job.responsibilities : [""],
    requirements: job.requirements?.length ? job.requirements : [""],
    nice_to_have: job.nice_to_have?.length ? job.nice_to_have : [""],
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, i, v) => setForm((f) => ({ ...f, [k]: f[k].map((x, idx) => idx === i ? v : x) }));
  const addLine = (k) => setForm((f) => ({ ...f, [k]: [...f[k], ""] }));
  const removeLine = (k, i) => setForm((f) => ({ ...f, [k]: f[k].filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.title.trim() || !form.summary.trim()) {
      setErr("Title and summary are required.");
      return;
    }
    const payload = {
      ...form,
      responsibilities: form.responsibilities.filter((x) => x.trim()),
      requirements: form.requirements.filter((x) => x.trim()),
      nice_to_have: form.nice_to_have.filter((x) => x.trim()),
      closes_at: form.closes_at || null,
    };
    setBusy(true);
    try {
      if (job.id) {
        await api.patch(`/admin/jobs/${job.id}`, payload, authHeaders());
      } else {
        await api.post("/admin/jobs", payload, authHeaders());
      }
      onSaved();
    } catch (e2) {
      setErr(apiErrorMessage(e2, "Save failed."));
    } finally { setBusy(false); }
  };

  return (
    <section className="min-h-screen bg-wafa-cream/30" data-testid="job-editor">
      <div className="mx-auto max-w-4xl px-4 py-8 lg:px-6 lg:py-12">
        <button onClick={onCancel} className="mb-4 inline-flex items-center gap-1 text-sm text-wafa-ink/70 hover:text-wafa-deep" data-testid="job-editor-back">
          <ArrowLeft className="h-4 w-4" /> Back to list
        </button>
        <h1 className="font-heading text-3xl font-semibold text-wafa-deep">
          {job.id ? `Edit — ${job.title}` : "New job"}
        </h1>

        <form onSubmit={submit} className="mt-8 space-y-6">
          <Row>
            <TextField label="Title *" value={form.title} onChange={(v) => set("title", v)} testid="job-title" />
            <TextField label="Slug (auto if blank)" value={form.slug} onChange={(v) => set("slug", v)} testid="job-slug" />
          </Row>
          <Row>
            <TextField label="Department *" value={form.department} onChange={(v) => set("department", v)} testid="job-department" />
            <TextField label="Location *" value={form.location} onChange={(v) => set("location", v)} testid="job-location" />
          </Row>
          <Row>
            <SelectField label="Employment type" value={form.employment_type} onChange={(v) => set("employment_type", v)} options={EMP_TYPES} testid="job-employment-type" />
            <SelectField label="Experience level" value={form.experience_level} onChange={(v) => set("experience_level", v)} options={EXP_LEVELS} testid="job-experience-level" />
          </Row>
          <TextField label="Summary * (1–2 lines shown on the listing card)" value={form.summary} onChange={(v) => set("summary", v)} testid="job-summary" />
          <Textarea label="Description" value={form.description} onChange={(v) => set("description", v)} rows={6} testid="job-description" />
          <ListField label="Responsibilities" values={form.responsibilities} setValue={(i, v) => setList("responsibilities", i, v)} add={() => addLine("responsibilities")} remove={(i) => removeLine("responsibilities", i)} testid="job-responsibilities" />
          <ListField label="Requirements" values={form.requirements} setValue={(i, v) => setList("requirements", i, v)} add={() => addLine("requirements")} remove={(i) => removeLine("requirements", i)} testid="job-requirements" />
          <ListField label="Nice to have (optional)" values={form.nice_to_have} setValue={(i, v) => setList("nice_to_have", i, v)} add={() => addLine("nice_to_have")} remove={(i) => removeLine("nice_to_have", i)} testid="job-nice-to-have" />
          <Row>
            <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={JOB_STATUSES} testid="job-status" />
            <TextField label="Closes at (optional, YYYY-MM-DD)" value={form.closes_at || ""} onChange={(v) => set("closes_at", v)} testid="job-closes-at" />
          </Row>

          {err ? <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="job-editor-error">{err}</div> : null}

          <div className="flex items-center justify-end gap-2 border-t border-wafa-deep/10 pt-6">
            <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 rounded-md border border-wafa-deep/15 px-4 py-2 text-sm hover:bg-white" data-testid="job-editor-cancel">
              <X className="h-4 w-4" /> Cancel
            </button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-wafa-primary px-4 py-2 text-sm font-semibold text-white hover:bg-wafa-deep disabled:opacity-60" data-testid="job-editor-save">
              <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Row({ children }) { return <div className="grid gap-4 sm:grid-cols-2">{children}</div>; }
function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function Textarea({ label, value, onChange, rows = 4, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid} className="mt-2 w-full rounded-md border border-wafa-deep/15 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
    </div>
  );
}
function ListField({ label, values, setValue, add, remove, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">{label}</label>
      <div className="mt-2 space-y-2" data-testid={testid}>
        {values.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={v} onChange={(e) => setValue(i, e.target.value)} data-testid={`${testid}-${i}`} className="h-10 flex-1 rounded-md border border-wafa-deep/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
            {values.length > 1 ? (
              <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="rounded p-1.5 text-wafa-ink/50 hover:bg-red-50 hover:text-red-700">
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
      <button type="button" onClick={add} data-testid={`${testid}-add`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-wafa-primary hover:text-wafa-deep">
        <Plus className="h-3.5 w-3.5" /> Add line
      </button>
    </div>
  );
}
